import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ShareButtonsModule } from 'ngx-sharebuttons/buttons';
import { ShareIconsModule } from 'ngx-sharebuttons/icons';
import { clearOrphanedModalState } from '../../../core/util/modal-state.util';
import { CxIconComponent } from '../../../shared/icon/cx-icon.component';
declare var $: any;

export interface ShareDestination {
  id: string;
  label: string;
  description: string;
  url: string;
  recommended?: boolean;
}

export interface ShareModalOptions {
  currentUrl: string;
  destinations?: ShareDestination[];
}

@Component({
  selector: 'app-share-modal',
  standalone: true,
  imports: [
    CommonModule,
    ShareButtonsModule,
    ShareIconsModule,
    CxIconComponent,
  ],
  templateUrl: './share-modal.component.html',
  styleUrl: './share-modal.component.scss',
})
export class ShareModalComponent implements AfterViewInit, OnDestroy {
  @Input() currentUrl = '';
  @Input() backdrop: boolean | 'static' = 'static';
  @Input() keyboard = false;

  @ViewChild('shareModal', { static: true }) shareModal!: ElementRef;
  private isOpen = false;
  private modalTransition: 'opening' | 'closing' | null = null;
  private openAfterTransition = false;
  private pendingOptions?: ShareModalOptions;
  private restoreFocusTarget: HTMLElement | null = null;
  shareDestinations: ShareDestination[] = [];
  selectedDestinationId = '';
  activeShareUrl = '';

  ngAfterViewInit(): void {
    this.getModal()
      ?.on('show.bs.modal.shareModal', () => {
        this.modalTransition = 'opening';
      })
      .on('shown.bs.modal.shareModal', () => {
        this.modalTransition = null;
        this.isOpen = true;
      })
      .on('hide.bs.modal.shareModal', () => {
        this.modalTransition = 'closing';
        this.isOpen = false;
      })
      .on('hidden.bs.modal.shareModal', () => {
        this.modalTransition = null;
        clearOrphanedModalState();

        if (this.openAfterTransition) {
          this.openAfterTransition = false;
          const options = this.pendingOptions;
          this.pendingOptions = undefined;
          this.openModal(options);
          return;
        }

        this.restoreFocus();
      });
  }

  openModal(options?: ShareModalOptions): void {
    if (this.modalTransition === 'closing') {
      this.openAfterTransition = true;
      this.pendingOptions = options;
      return;
    }

    if (this.isOpen || this.modalTransition === 'opening') {
      return;
    }

    this.configureShare(options);
    this.captureFocus();
    this.getModal()?.modal('show');
  }

  updateShare(options: ShareModalOptions): void {
    this.configureShare(options);
  }

  selectDestination(destination: ShareDestination): void {
    this.selectedDestinationId = destination.id;
    this.activeShareUrl = destination.url;
  }

  closeModal(): void {
    this.openAfterTransition = false;
    this.pendingOptions = undefined;
    if (this.isOpen) {
      this.getModal()?.modal('hide');
    }
  }

  onShareOpened(): void {
    this.closeModal();
  }

  copyToClipboard(inputElement: HTMLInputElement) {
    inputElement.select();
    document.execCommand('copy');
  }

  ngOnDestroy(): void {
    this.getModal()?.off('.shareModal');
    // Navigation performs the final modal cleanup through AppComponent.
  }

  private getModal(): any {
    return this.shareModal?.nativeElement
      ? $(this.shareModal.nativeElement)
      : null;
  }

  private configureShare(options?: ShareModalOptions): void {
    this.shareDestinations = [...(options?.destinations ?? [])];
    const initialDestination =
      this.shareDestinations.find((destination) => destination.recommended) ??
      this.shareDestinations[0];

    this.selectedDestinationId = initialDestination?.id ?? '';
    this.activeShareUrl = initialDestination?.url ?? options?.currentUrl ?? '';
  }

  private captureFocus(): void {
    const activeElement = document.activeElement;
    this.restoreFocusTarget =
      activeElement instanceof HTMLElement && activeElement !== document.body
        ? activeElement
        : null;
  }

  private restoreFocus(): void {
    if (this.restoreFocusTarget?.isConnected) {
      this.restoreFocusTarget.focus({ preventScroll: true });
    }
    this.restoreFocusTarget = null;
  }
}
